import { Controller } from "@hotwired/stimulus"

// Validiert Dokumentennummern live im Formular.
// Kein Hard-Block – nur visuelles Feedback (grün/gelb).
//
// Unterstützte Typen:
//   Reisepass        – 9 alphanumerische Zeichen, kein Prüfziffer-Check
//   Personalausweis  – 10 Zeichen, letztes Zeichen ist ICAO-Prüfziffer
//
// Usage:
//   data-controller="passport-number" auf dem umschließenden Container
//   data-passport-number-target="number"  auf dem Eingabefeld
//   data-passport-number-target="docType" auf jedem Dokumenttyp-Radio (mehrere erlaubt)
//   data-passport-number-target="feedback" auf einem leeren <div> unterhalb des Feldes

export default class extends Controller {
  static targets = ["number", "docType", "feedback"]

  connect() {
    this.validate()
  }

  validate() {
    const raw   = this.numberTarget.value
    const value = raw.toUpperCase().replace(/\s/g, "")

    if (!value) {
      this.clearFeedback()
      return
    }

    const docType = this.selectedDocType()
    const result  = this.checkFormat(value, docType)
    this.showFeedback(result)
  }

  // ── private ──────────────────────────────────────────────────────────────

  selectedDocType() {
    if (!this.hasDocTypeTarget) return null
    const checked = this.docTypeTargets.find(r => r.checked)
    return checked ? checked.value : null
  }

  checkFormat(value, docType) {
    if (!/^[A-Z0-9]+$/.test(value)) {
      return { valid: false, message: "Nur Buchstaben (A–Z) und Ziffern erlaubt" }
    }

    if (docType === "Reisepass") {
      if (value.length !== 9) {
        return { valid: false, message: `Reisepass: 9 Zeichen erwartet (${value.length} eingegeben)` }
      }
      return { valid: true, message: "Format gültig (Reisepass)" }
    }

    if (docType === "Personalausweis") {
      if (value.length !== 10) {
        return { valid: false, message: `Personalausweis: 10 Zeichen erwartet (${value.length} eingegeben)` }
      }
      if (!this.icaoCheckDigitValid(value.slice(0, 9), parseInt(value[9], 10))) {
        return { valid: false, message: "Prüfziffer stimmt nicht – bitte Nummer prüfen" }
      }
      return { valid: true, message: "Format und Prüfziffer gültig" }
    }

    // Kein Dokumenttyp gewählt – nur Plausibilitätsprüfung
    if (value.length >= 8 && value.length <= 10) {
      return { valid: true, message: "Format plausibel" }
    }
    return { valid: false, message: `Ungewöhnliche Länge (${value.length} Zeichen) – bitte prüfen` }
  }

  // ICAO 9303 Prüfzifferalgorithmus
  // Gewichte: 7-3-1 zyklisch; A=10, B=11 ... Z=35; Ziffern = Zahlenwert
  icaoCheckDigitValid(nineChars, expectedDigit) {
    const weights = [7, 3, 1]
    let sum = 0

    for (let i = 0; i < nineChars.length; i++) {
      const c = nineChars[i]
      let val
      if (c >= "0" && c <= "9") {
        val = parseInt(c, 10)
      } else if (c >= "A" && c <= "Z") {
        val = c.charCodeAt(0) - "A".charCodeAt(0) + 10
      } else {
        return false
      }
      sum += val * weights[i % 3]
    }

    return (sum % 10) === expectedDigit
  }

  showFeedback(result) {
    if (result.valid) {
      this.feedbackTarget.innerHTML =
        `<span class="text-success small"><i class="bi bi-check-circle-fill me-1"></i>${result.message}</span>`
    } else {
      this.feedbackTarget.innerHTML =
        `<span class="text-warning small"><i class="bi bi-exclamation-triangle-fill me-1"></i>${result.message}</span>`
    }
  }

  clearFeedback() {
    this.feedbackTarget.innerHTML = ""
  }
}
